import {
  Button, Card, Input, Modal, Select, Space, Table, Typography,
} from 'antd';
import rentalApi from 'api/rentalApi';
import ToastHelper from 'general/helpers/ToastHelper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DAMAGE_STATUS_OPTIONS, DamageSeverityTag, DamageStatusTag, formatDateTime, getRentalErrorMessage,
} from '../../rentalConstants';

const { Text, Title } = Typography;

function RentalDamageReportListScreen() {
  const [status, setStatus] = useState('OPEN');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [resolveModal, setResolveModal] = useState(null);
  const [resolutionNote, setResolutionNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await rentalApi.getDamageReports({ status: status || undefined, page, pageSize });
      setRows(response?.damageReports || []);
      setTotal(response?.total || 0);
    } catch (error) {
      setRows([]);
      setTotal(0);
      ToastHelper.showError(getRentalErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [status, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const submitResolve = async () => {
    if (!resolutionNote.trim()) return;
    setActionLoading(true);
    try {
      await rentalApi.resolveDamageReport(resolveModal.rentalDamageReportId, resolutionNote.trim());
      ToastHelper.showSuccess('Đã đánh dấu đã xử lý');
      setResolveModal(null);
      setResolutionNote('');
      load();
    } catch (error) {
      ToastHelper.showError(getRentalErrorMessage(error));
    } finally {
      setActionLoading(false);
    }
  };

  const columns = useMemo(() => [
    { title: 'Mã BC', dataIndex: 'rentalDamageReportId', width: 90, render: (value) => <Text strong>#{value}</Text> },
    { title: 'Đơn thuê', key: 'order', render: (_, row) => row.order?.orderCode || `#${row.rentalOrderId}` },
    { title: 'Xe', key: 'vehicle', render: (_, row) => `${row.vehicle?.vehicleCode || ''} · ${row.vehicle?.plate || ''}` },
    { title: 'Mức độ', dataIndex: 'severity', width: 100, render: (value) => <DamageSeverityTag severity={value} /> },
    { title: 'Mô tả', dataIndex: 'description', ellipsis: true },
    { title: 'Trạng thái', dataIndex: 'status', width: 130, render: (value) => <DamageStatusTag status={value} /> },
    { title: 'Tạo lúc', dataIndex: 'createdAt', width: 170, render: formatDateTime },
    {
      title: '',
      key: 'action',
      width: 130,
      render: (_, row) => (row.status === 'OPEN' ? (
        <Button type="link" onClick={() => setResolveModal(row)}>Đánh dấu đã xử lý</Button>
      ) : null),
    },
  ], []);

  return (
    <div className="container-fluid px-4 my-4">
      <Card>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
          <div>
            <Title level={4} className="mb-0">Báo cáo hư hỏng xe cho thuê</Title>
            <Text type="secondary">Tổng {total} báo cáo theo bộ lọc</Text>
          </div>
          <Button onClick={load} loading={loading}>Làm mới</Button>
        </div>
        <Space wrap className="mb-4">
          <Select
            value={status}
            options={DAMAGE_STATUS_OPTIONS}
            style={{ width: 190 }}
            onChange={(value) => { setStatus(value); setPage(1); }}
          />
        </Space>
        <Table
          rowKey="rentalDamageReportId"
          loading={loading}
          dataSource={rows}
          columns={columns}
          scroll={{ x: 1000 }}
          locale={{ emptyText: 'Không có dữ liệu' }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextPageSize) => { setPage(nextPage); setPageSize(nextPageSize); },
          }}
        />
      </Card>

      <Modal
        title={`Xử lý báo cáo #${resolveModal?.rentalDamageReportId || ''}`}
        open={Boolean(resolveModal)}
        okText="Xác nhận đã xử lý"
        cancelText="Huỷ"
        confirmLoading={actionLoading}
        okButtonProps={{ disabled: !resolutionNote.trim() }}
        onCancel={() => { setResolveModal(null); setResolutionNote(''); }}
        onOk={submitResolve}
      >
        <Input.TextArea
          rows={4}
          maxLength={2000}
          showCount
          value={resolutionNote}
          placeholder="Ghi chú cách đã xử lý (chi phí sửa, bồi thường, bảo hiểm...)"
          onChange={(event) => setResolutionNote(event.target.value)}
        />
      </Modal>
    </div>
  );
}

export default RentalDamageReportListScreen;
